'use client';

import iconPalette from '@/assets/images/icons/icon-palette.png';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import RoundTextIcon from '@/components/ui/RoundTextIcon/RoundTextIcon';
import SpeechBubbleTextIcon from '@/components/ui/SpeechBubbleTextIcon/SpeechBubbleTextIcon';
import { PRODUCT_NAME, SERVICE_OFFICIAL_SNS_URL_X } from '@/constants';
import { useGlobalLoadingAtom } from '@/state';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { BadgeStatus, type GfWork } from '@/types';
import { formatCreatorNames } from '@/utils/formatters';
import { sendWorkNavigationFromTopEvent } from '@/utils/ga';
import { getImageLayoutSizes } from '@/utils/nextImage';
import type { Route } from 'next';
import { useSession } from 'next-auth/react';
import { usePathname, useRouter } from 'next/navigation';
import styles from './WorkCardList.module.scss';

type WorkCardListProps = {
  works: GfWork[];
  showComingSoon?: boolean;
  workLinkPrefix?: string;
  workLinkParam?: Record<string, string>;
};

function WorkCardList({
  works,
  showComingSoon = true,
  workLinkPrefix = '/works',
  workLinkParam = {},
}: WorkCardListProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [_, startGlobalLoading] = useGlobalLoadingAtom();
  const session = useSession();

  const handleWorkClick = (workId: number, workName: string) => {
    startGlobalLoading();

    // GAイベント送信（TOPページからの遷移の場合のみ、router.pushより前に発火）
    const isFromTopPage = pathname === '/';
    if (isFromTopPage) {
      const userId = session?.data?.user.id ?? 0;
      sendWorkNavigationFromTopEvent(workId, workName, userId);
    }

    const basePath = `${workLinkPrefix}/${workId}`;
    if (Object.keys(workLinkParam).length > 0) {
      const queryString = new URLSearchParams(workLinkParam).toString();
      const url = `${basePath}?${queryString}`;
      router.push(url as Route);
    } else {
      router.push(basePath as Route);
    }
  };

  return (
    <div className={styles.cardContainer}>
      {works.length > 0 &&
        works.map((work) => (
          <div key={work.id} className={styles.cardLink}>
            <div className={styles.cardWrapper}>
              <button
                type='button'
                onClick={() => handleWorkClick(work.id, work.name)}
                className={styles.imageLink}
                data-testid={`work-button-${work.id}-${work.name.replace(/[\s\-]/g, '')}`}
              >
                {work.badgeStatus === BadgeStatus.new.value && (
                  <div className={styles.badgeStatus}>
                    <RoundTextIcon
                      bgColor={BadgeStatus[work.badgeStatus].color}
                      text={BadgeStatus[work.badgeStatus].text}
                      size='large'
                    />
                  </div>
                )}
                {work.badgeStatus === BadgeStatus.added.value && (
                  <div className={styles.badgeStatus}>
                    <SpeechBubbleTextIcon
                      bgColor={BadgeStatus[work.badgeStatus].color}
                      text={BadgeStatus[work.badgeStatus].text}
                      size='small'
                      direction='right'
                    />
                  </div>
                )}
                <NextImageView
                  src={work.imagePath}
                  alt={work.name}
                  containerClassName={styles.imageContainer}
                  fill={false}
                  width={204}
                  height={300}
                  objectFit='contain'
                  sizes={getImageLayoutSizes({ sp: '33vw', pc: '280px' })}
                />
              </button>
              <button
                type='button'
                onClick={() => handleWorkClick(work.id, work.name)}
                className={styles.workInfo}
                data-testid={`work-info-button-${work.id}-${work.name.replace(/[\s\-]/g, '')}`}
              >
                <span className={styles.workName}>{work.name}</span>
                <span className={styles.creatorName}>{formatCreatorNames(work.creators)}</span>
              </button>
            </div>
          </div>
        ))}
      {/* {showComingSoon && (
        <div className={`${styles.comingSoonCardWrapper}`}>
          <div className={styles.comingSoonCard}>
            <div className={`${styles.comingSoonCardContent}`}>
              <img src={iconPalette.src} alt='icon' className={`${styles.iconCommon}`} />
              <div className={styles.comingSoonTitle}>
                <span>ジャンプ LAB</span>
                <span>X (旧Twitter)</span>
              </div>
              <div className={styles.comingSoonDescription}>作品続々追加予定！</div>
              <div className={styles.comingSoonSubDescription}>
                詳しくはXをフォローして
                <br className={utilsStyles.onlyPc} />
                チェック!
              </div>
            </div>
          </div>
        </div>
      )} */}
      {showComingSoon && (
        <a
          href={SERVICE_OFFICIAL_SNS_URL_X}
          target='_blank'
          rel='noopener noreferrer'
          className={`${styles.comingSoonCardWrapper}`}
        >
          <div className={styles.comingSoonCard}>
            <div className={`${styles.comingSoonCardContent}`}>
              <img src={iconPalette.src} alt='icon' className={`${styles.iconCommon}`} />
              <div className={styles.comingSoonTitle}>
                <span>{PRODUCT_NAME}</span>
                <span>X (旧Twitter)</span>
              </div>
              <div className={styles.comingSoonDescription}>作品続々追加予定！</div>
              <div className={styles.comingSoonSubDescription}>
                詳しくはXをフォローして
                <br className={utilsStyles.onlyPc} />
                チェック!
              </div>
            </div>
          </div>
        </a>
      )}
    </div>
  );
}

export default WorkCardList;
